import { useMutation, useQueryClient } from '@tanstack/react-query';
import QualAPI from 'utils/QualAPI';
import { userKeys } from './queries';

/**
 * Hook to add a collaborator to a project
 * @returns {Object} Mutation object for adding collaborators
 */
export function useAddCollaborator() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ projectId, email, role }) => 
      QualAPI.addCollborator(projectId, email, role),
    onSuccess: (data, { projectId }) => {
      // Invalidate the project users list to trigger a refetch
      queryClient.invalidateQueries({ queryKey: userKeys.list(projectId) });
    },
  });
}

/**
 * Hook to remove a collaborator from a project
 * @returns {Object} Mutation object for removing collaborators
 */
export function useRemoveCollaborator() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ projectId, collaboratorId }) => 
      QualAPI.removeCollaborator(projectId, collaboratorId),
    onSuccess: (data, { projectId }) => {
      // Invalidate the project users list to trigger a refetch
      queryClient.invalidateQueries({ queryKey: userKeys.list(projectId) });
    },
  });
}

/**
 * Hook to update a collaborator's role in a project
 * @returns {Object} Mutation object for updating collaborator roles
 */
export function useUpdateCollaborator() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ projectId, collaboratorId, role }) => 
      QualAPI.updateCollaborator(projectId, collaboratorId, role),
    onSuccess: (data, { projectId }) => {
      // Invalidate the project users list to trigger a refetch
      queryClient.invalidateQueries({ queryKey: userKeys.list(projectId) });
    },
  });
}

/**
 * Hook to add a seat to a subscription
 * @returns {Object} Mutation object for adding seats
 */
export function useAddSeat() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ subscriptionId, email }) => 
      QualAPI.addSeat(subscriptionId, email),
    onSuccess: (data, { projectId }) => {
      // Invalidate all user queries to refresh subscription status
      queryClient.invalidateQueries({ queryKey: userKeys.all });
      // If projectId is provided, specifically invalidate that project's users
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: userKeys.list(projectId) });
      }
    },
  });
}

/**
 * Hook to accept terms of service
 * @returns {Object} Mutation object for accepting terms
 */
export function useAcceptTermsOfService() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => QualAPI.acceptTermsOfService(),
    onSuccess: (response) => {
      // Update the current user data with the response
      if (response.data) {
        queryClient.setQueryData(userKeys.current(), response.data);
        // Also update the individual user cache if ID is present
        if (response.data.id) {
          queryClient.setQueryData(userKeys.detail(response.data.id), response.data);
        }
      }
    },
  });
}